import QueryString from "query-string";
import { all, call, put, select, takeEvery } from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import {
  addCareRoundFailure,
  addCareRoundSuccess,
  deleteCareRoundFailure,
  deleteCareRoundSuccess,
  editCareRoundFailure,
  editCareRoundSuccess,
  fetchCareRoundFailure,
  fetchCareRoundItemFailure,
  fetchCareRoundItemSuccess,
  fetchCareRoundOccurrenceReportFailure,
  fetchCareRoundOccurrenceReportSuccess,
  fetchCareRoundNextTwoDaysSummaryFailure,
  fetchCareRoundNextTwoDaysSummarySuccess,
  fetchCareRoundReportFailure,
  fetchCareRoundReportSuccess,
  fetchCareRoundSuccess,
  fetchFacilityDailyReportSuccess,
  fetchFacilityDailyReportFailure,
  fetchFacilityTwiceDailyReportSuccess,
  fetchFacilityTwiceDailyReportFailure,
  fetchFacilityWeeklyReportSuccess,
  fetchFacilityWeeklyReportFailure,
  fetchFacilityIntervalReportSuccess,
  fetchFacilityIntervalReportFailure,
  markRoundTaskCompletionSuccess,
  markRoundTaskCompletionFailure,
  saveRoundDateAssignmentSuccess,
  saveRoundDateAssignmentFailure,
  fetchRoundAssignmentSummarySuccess,
  fetchRoundAssignmentSummaryFailure,
} from "./actions";
import {
  ADD_CARE_ROUND,
  DELETE_CARE_ROUND,
  EDIT_CARE_ROUND,
  FETCH_CARE_ROUND,
  FETCH_CARE_ROUND_ITEM,
  FETCH_CARE_ROUND_NEXT_TWO_DAYS_SUMMARY,
  FETCH_CARE_ROUND_OCCURRENCE_REPORT,
  FETCH_CARE_ROUND_REPORT,
  FETCH_FACILITY_DAILY_REPORT,
  FETCH_FACILITY_TWICE_DAILY_REPORT,
  FETCH_FACILITY_WEEKLY_REPORT,
  FETCH_FACILITY_INTERVAL_REPORT,
  FETCH_ROUND_ASSIGNMENT_SUMMARY,
  MARK_ROUND_TASK_COMPLETION,
  SAVE_ROUND_DATE_ASSIGNMENT,
} from "./constants";

const SETTINGS_PATH = "/d/resident/care-rounds";

const extractFieldErrors = (errorLike) => {
  const raw = errorLike?.response?.data;
  const messageBag =
    raw?.message &&
    typeof raw.message === "object" &&
    !Array.isArray(raw.message)
      ? raw.message
      : raw?.error && typeof raw.error === "object" && !Array.isArray(raw.error)
        ? raw.error
        : null;
  if (!messageBag) return null;
  const fieldErrors = {};
  Object.entries(messageBag).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      fieldErrors[key] = value.filter(Boolean).join(", ");
    } else if (typeof value === "string") {
      fieldErrors[key] = value;
    } else if (value != null) {
      fieldErrors[key] = String(value);
    }
  });
  return Object.keys(fieldErrors).length > 0 ? fieldErrors : null;
};

export function* fetchCareRoundSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = payload ? QueryString.stringify(payload) : "";
  try {
    const { data } = yield call(api(token).get, `/api/round?${query}`);
    const result = Array.isArray(data)
      ? { data, total: data.length, current_page: payload?.page ?? 1 }
      : { ...data, current_page: payload?.page ?? data?.current_page ?? 1 };
    yield put(fetchCareRoundSuccess(result));
  } catch (e) {
    yield put(fetchCareRoundFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to fetch care rounds"));
  }
}

export function* addCareRoundSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { data, history, onSuccess, onError } = payload || {};
  try {
    const response = yield call(api(token).post, `/api/round/add`, data);
    if (response) {
      yield put(addCareRoundSuccess());
      yield put(successAlert("Care round created successfully"));
      if (typeof onSuccess === "function") onSuccess();
      else if (history) history.push(SETTINGS_PATH);
    }
  } catch (e) {
    yield put(addCareRoundFailure());
    const fieldErrors = extractFieldErrors(e);
    if (fieldErrors && typeof onError === "function") {
      onError(fieldErrors);
      return;
    }
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to create care round"));
  }
}

export function* fetchCareRoundItemSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const id =
    typeof payload === "string" ? payload : (payload?.uuid ?? payload?.id);
  try {
    const { data } = yield call(api(token).get, `/api/round/${id}`);
    yield put(fetchCareRoundItemSuccess(data?.data ?? data));
  } catch (e) {
    yield put(fetchCareRoundItemFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to fetch care round"));
  }
}

export function* fetchCareRoundReportSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const queryPayload =
    payload && typeof payload === "object"
      ? Object.entries(payload).reduce((acc, [key, value]) => {
          if (!["id", "uuid"].includes(key) && value != null) {
            acc[key] = value;
          }
          return acc;
        }, {})
      : {};
  const query = QueryString.stringify(queryPayload);

  try {
    const { data } = yield call(
      api(token).get,
      `/api/round/schedule${query ? `?${query}` : ""}`,
    );
    yield put(fetchCareRoundReportSuccess(data?.data ?? data));
  } catch (e) {
    yield put(fetchCareRoundReportFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to fetch care round report"));
  }
}

export function* fetchCareRoundNextTwoDaysSummarySaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { location_id, summary_from, summary_to } = payload || {};

  if (!location_id || !summary_from || !summary_to) {
    yield put(fetchCareRoundNextTwoDaysSummaryFailure());
    return;
  }

  const query = QueryString.stringify({
    location_id,
    summary_from,
    summary_to,
  });

  try {
    const { data } = yield call(
      api(token).get,
      `/api/round/schedule/next-two-days-summary?${query}`,
    );
    const result = data?.data ?? data;
    yield put(
      fetchCareRoundNextTwoDaysSummarySuccess({
        ...(result && typeof result === "object" ? result : {}),
        summary_from: result?.summary_from ?? summary_from,
        summary_to: result?.summary_to ?? summary_to,
      }),
    );
  } catch (e) {
    yield put(fetchCareRoundNextTwoDaysSummaryFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to fetch care round summary"));
  }
}

export function* fetchCareRoundOccurrenceReportSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const roundUuid =
    payload?.round_uuid || payload?.roundUuid || payload?.round?.id;
  const occurrenceUuid =
    payload?.occurrence_uuid ||
    payload?.occurrenceUuid ||
    payload?.occurrence_id;

  if (!roundUuid) {
    yield put(fetchCareRoundOccurrenceReportFailure());
    return;
  }

  // For synthetic slots (no occurrence UUID), pass the date derived from scheduled_at
  // so the API returns only that day's data instead of the full history.
  const date =
    !occurrenceUuid && payload?.scheduled_at
      ? payload.scheduled_at.slice(0, 10)
      : undefined;

  const time =
    payload?.time ||
    payload?.scheduled_time_short ||
    (payload?.scheduled_at ? payload.scheduled_at.slice(11, 16) : undefined);

  const all = payload?.all ? 1 : undefined;

  const queryParams = {
    ...(occurrenceUuid ? { occurrence_uuid: occurrenceUuid } : {}),
    ...(date ? { date } : {}),
    ...(time ? { time } : {}),
    ...(all ? { all } : {}),
  };
  const query = QueryString.stringify(queryParams);

  try {
    const response = yield call(
      api(token).get,
      `/api/round/${roundUuid}/report${query ? `?${query}` : ""}`,
    );
    yield put(
      fetchCareRoundOccurrenceReportSuccess(response?.data ?? response),
    );
  } catch (e) {
    console.error("Failed to fetch care round occurrence report", e);
    yield put(fetchCareRoundOccurrenceReportFailure());
  }
}

export function* editCareRoundSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { data, history, id, uuid, onSuccess, onError } = payload || {};
  const itemId = id ?? uuid;
  try {
    const response = yield call(
      api(token).post,
      `/api/round/edit/${itemId}`,
      data,
    );
    if (response) {
      yield put(editCareRoundSuccess());
      yield put(successAlert("Care round updated successfully"));
      if (typeof onSuccess === "function") onSuccess();
      else if (history) history.push(SETTINGS_PATH);
    }
  } catch (e) {
    yield put(editCareRoundFailure());
    const fieldErrors = extractFieldErrors(e);
    if (fieldErrors && typeof onError === "function") {
      onError(fieldErrors);
      return;
    }
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to update care round"));
  }
}

export function* deleteCareRoundSaga({ payload, onDelete }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const id = payload?.id ?? payload?.uuid;
  try {
    const data = yield call(api(token).delete, `/api/round/${id}`);
    if (data) {
      yield put(deleteCareRoundSuccess());
      yield put(successAlert("Care round deleted successfully"));
      typeof onDelete === "function" && onDelete();
    }
  } catch (e) {
    yield put(deleteCareRoundFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to delete care round"));
  }
}

export function* fetchFacilityDailyReportSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { silent, ...apiParams } = payload || {};
  try {
    const data = yield call(
      api(token).get,
      `/api/round/facility-daily-report`,
      {
        params: apiParams,
      },
    );
    yield put(fetchFacilityDailyReportSuccess(data?.data ?? data));
  } catch (e) {
    yield put(fetchFacilityDailyReportFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to fetch facility daily report"));
  }
}

export function* fetchFacilityTwiceDailyReportSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { silent, ...apiParams } = payload || {};
  try {
    const data = yield call(
      api(token).get,
      `/api/round/facility-twice-daily-report`,
      { params: apiParams },
    );
    yield put(fetchFacilityTwiceDailyReportSuccess(data?.data ?? data));
  } catch (e) {
    yield put(fetchFacilityTwiceDailyReportFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to fetch facility twice daily report"));
  }
}

export function* fetchFacilityWeeklyReportSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { silent, ...apiParams } = payload || {};
  try {
    const data = yield call(
      api(token).get,
      `/api/round/facility-weekly-report`,
      { params: apiParams },
    );
    yield put(fetchFacilityWeeklyReportSuccess(data?.data ?? data));
  } catch (e) {
    yield put(fetchFacilityWeeklyReportFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to fetch facility weekly report"));
  }
}

export function* fetchFacilityIntervalReportSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { silent, ...apiParams } = payload || {};
  try {
    const data = yield call(
      api(token).get,
      `/api/round/facility-interval-report`,
      { params: apiParams },
    );
    yield put(fetchFacilityIntervalReportSuccess(data?.data ?? data));
  } catch (e) {
    yield put(fetchFacilityIntervalReportFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to fetch facility interval report"));
  }
}

function* markRoundTaskCompletionSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const employeeId = yield select((state) => state?.AuthReducer?.user?.id);
  const {
    roundId,
    taskId,
    isCompleted,
    isSkipped,
    residentId,
    scheduledAt,
    notes,
  } = payload;
  try {
    const body = {
      employee_id: employeeId,
      task_id: taskId,
      is_completed: isCompleted,
      is_skipped: isSkipped ?? false,
    };
    if (residentId) body.resident_id = residentId;
    if (scheduledAt) body.scheduled_at = scheduledAt;
    if (notes != null && String(notes).trim() !== "") {
      body.notes = String(notes).trim();
    }
    const { data } = yield call(
      api(token).post,
      `/api/round/${roundId}/task-completion`,
      body,
    );
    yield put(markRoundTaskCompletionSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(markRoundTaskCompletionFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Error updating task status"));
    typeof onSuccess === "function" && onSuccess(null);
  }
}

function* saveRoundDateAssignmentSaga({ payload, onSuccess, onFailure }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { roundId, assignments } = payload;
  try {
    const body = {
      assignments: assignments.map((a) => {
        const careTeamIds = a.careTeamIds ?? [];
        const employeeIds = a.employeeIds ?? [];
        const entry = {
          assigned_date: a.assignedDate,
          is_team: careTeamIds.length > 0,
          care_team_ids: careTeamIds,
          is_employee: employeeIds.length > 0,
          employee_ids: employeeIds,
          actual_employee_ids: a.actualEmployeeIds ?? [],
        };
        if (a.residentGroupId) entry.resident_group_id = a.residentGroupId;
        return entry;
      }),
    };
    const { data } = yield call(
      api(token).post,
      `/api/round/${roundId}/date-assignment`,
      body,
    );
    yield put(saveRoundDateAssignmentSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(saveRoundDateAssignmentFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to save date assignment"));
    typeof onFailure === "function" && onFailure(e);
  }
}

function* fetchRoundAssignmentSummarySaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = QueryString.stringify(payload);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/round/assignment-summary?${query}`,
    );
    yield put(fetchRoundAssignmentSummarySuccess(data?.data ?? data));
  } catch (e) {
    yield put(fetchRoundAssignmentSummaryFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to fetch assignment summary"));
  }
}

export default function* CareRoundSaga() {
  yield all([
    takeEvery(FETCH_CARE_ROUND, fetchCareRoundSaga),
    takeEvery(ADD_CARE_ROUND, addCareRoundSaga),
    takeEvery(FETCH_CARE_ROUND_ITEM, fetchCareRoundItemSaga),
    takeEvery(FETCH_CARE_ROUND_REPORT, fetchCareRoundReportSaga),
    takeEvery(
      FETCH_CARE_ROUND_NEXT_TWO_DAYS_SUMMARY,
      fetchCareRoundNextTwoDaysSummarySaga,
    ),
    takeEvery(
      FETCH_CARE_ROUND_OCCURRENCE_REPORT,
      fetchCareRoundOccurrenceReportSaga,
    ),
    takeEvery(EDIT_CARE_ROUND, editCareRoundSaga),
    takeEvery(DELETE_CARE_ROUND, deleteCareRoundSaga),
    takeEvery(FETCH_FACILITY_DAILY_REPORT, fetchFacilityDailyReportSaga),
    takeEvery(
      FETCH_FACILITY_TWICE_DAILY_REPORT,
      fetchFacilityTwiceDailyReportSaga,
    ),
    takeEvery(FETCH_FACILITY_WEEKLY_REPORT, fetchFacilityWeeklyReportSaga),
    takeEvery(FETCH_FACILITY_INTERVAL_REPORT, fetchFacilityIntervalReportSaga),
    takeEvery(MARK_ROUND_TASK_COMPLETION, markRoundTaskCompletionSaga),
    takeEvery(SAVE_ROUND_DATE_ASSIGNMENT, saveRoundDateAssignmentSaga),
    takeEvery(FETCH_ROUND_ASSIGNMENT_SUMMARY, fetchRoundAssignmentSummarySaga),
  ]);
}
